import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { CollectionState, FiltersMap } from '@wix/bex-core';
import { CollectionTableBaseProps } from '../CollectionTable';
import { runInAction } from 'mobx';
import {
  VirtualTableRepeater,
  VirtualTableRepeaterProps,
} from '../VirtualGridRepeater/VirtualTableRepeater';
import { Table } from '@wix/design-system';
import { InfiniteScrollTableBase } from './InfiniteScrollTableBase';
import { VirtualGridStateProvider } from '../VirtualGridRepeater/VirtualGridStateContext';
import { TableState } from '../../state';
import { useWixPatternsContainer } from '@wix/bex-core/react';
import { minimalRequiredWdsRuntimeCheck } from '../../minimalRequiredWdsRuntimeCheck';

export interface InfiniteScrollTableVirtualProps<T, F extends FiltersMap>
  extends CollectionTableBaseProps<T, F> {
  rowHeight?: number | ((item: T, index: number) => number);
  estimatedRowHeight?: number;
  state: CollectionState<T, F> | TableState<T, F>;
}

function _InfiniteScrollTableVirtual<T, F extends FiltersMap = FiltersMap>(
  props: InfiniteScrollTableVirtualProps<T, F>,
) {
  const { rowHeight, estimatedRowHeight, horizontalScroll = true } = props;
  const container = useWixPatternsContainer();

  if (process.env.NODE_ENV !== 'production') {
    // need this version because of style fixes on the WSR side
    minimalRequiredWdsRuntimeCheck({
      required: '^1.64.1',
      requiredBy: 'Cairo/InfiniteScrollTableVirtual',
    });
  }

  const { st, classes } =
    Table.dataTableRowVirtualStyle as import('@stylable/runtime').RuntimeStylesheet;

  const [state] = useState(() =>
    props.state instanceof TableState
      ? props.state
      : new TableState<T, F>({
          collection: props.state,
          container,
        }),
  );

  useEffect(() => {
    runInAction(() => {
      state.virtual.rowHeight = rowHeight;
    });
  }, [rowHeight]);

  useEffect(() => {
    if (estimatedRowHeight == null) {
      return;
    }
    runInAction(() => {
      state.virtual.estimatedRowHeight = estimatedRowHeight;
    });
  }, [estimatedRowHeight]);

  const [BodyElementType] = useState(
    () =>
      ({ children }: VirtualTableRepeaterProps) =>
        (
          <VirtualGridStateProvider value={state.virtual}>
            <VirtualTableRepeater>{children}</VirtualTableRepeater>
          </VirtualGridStateProvider>
        ),
  );

  return (
    <InfiniteScrollTableBase
      {...props}
      state={state}
      rowClass={st(classes.root, { horizontalScroll })}
      headerClass={st(classes.thead)}
      headerRowClass={st(classes.root, { horizontalScroll })}
      horizontalScroll={horizontalScroll}
      isApplyColumnWidthStyle={true}
      BodyElementType={BodyElementType}
    />
  );
}
/** @deprecated Use TableVirtual instead  */
export const InfiniteScrollTableVirtual = observer(_InfiniteScrollTableVirtual);

export const TableVirtual = InfiniteScrollTableVirtual;
export { InfiniteScrollTableVirtualProps as TableVirtualProps };
